import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { object, string } from 'yup';
import RightModalLayout from '../../../../layouts/Modal/RightModalLayout';
import ModalSection from '../ModalSection';
import FormProviderMC from '../../../../components/FormProviderMC';
import InputComponent from '../../../../components/InputMC';
import CardAgent from './CardAgent';
import { SIDEBAR_ACTIONS } from '../../../../../constants/actionsConstants';
import AgentModel from '../../../../models/AgentModel';
import agentsSeller from '../../../../../assets/images/chatbot_ai/agents_seller.svg';
import agentsSupport from '../../../../../assets/images/chatbot_ai/agents_support.svg';
import agentsPlanner from '../../../../../assets/images/chatbot_ai/agents_planner.svg';
import agentsCustom from '../../../../../assets/images/chatbot_ai/agents_custom.svg';

const CreateModal = ({ refetch, toggleAgentLimitModal, hasActiveAgent = false }) => {
  const { t } = useTranslation('ChatbotIA');
  const dispatch = useDispatch();
  const [selectedAgentType, setSelectedAgentType] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validationSchema = useMemo(() => object({
    name: string()
      .trim()
      .required(t('agents.create.name_required'))
  }), [t]);

  const methods = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      name: ''
    }
  });

  const { handleSubmit, watch } = methods;
  const nameValue = watch('name');

  const toggleCreateModal = () => {
    dispatch({ type: SIDEBAR_ACTIONS.CLOSE_SIDEBAR });
  };

  const onCreateAgent = (data) => {
    if (isSubmitting) return;

    setIsSubmitting(true);

    const businessModelMapping = {
      ecommerce: t('agents.types.ecommerce.subtitle'),
      tourism: t('agents.types.tourism.subtitle'),
      health_services: t('agents.types.health_services.subtitle'),
      custom: t('agents.types.custom.subtitle')
    };

    const agentData = {
      name: data.name.trim(),
      business_model: businessModelMapping[selectedAgentType] || selectedAgentType,
      type: selectedAgentType,
      version: '3',
      active: !hasActiveAgent,
      answer_with_products: false
    };

    AgentModel.save(agentData)
      .then(() => {
        showtoast(t('agents.successCreate'), 'success');
        toggleCreateModal();
        if (refetch) {
          refetch();
        }
      })
      .catch((error) => {
        if(error.status == 426) {
          toggleCreateModal();
          toggleAgentLimitModal();
        } else {
          showtoast(t('agents.errorCreate'), 'error');
        }
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const agentTypes = [
    {
      id: 'ecommerce',
      title: t('agents.types.ecommerce.title'),
      subtitle: t('agents.types.ecommerce.subtitle'),
      features: t('agents.types.ecommerce.features', { returnObjects: true }),
      image: agentsSeller
    },
    {
      id: 'tourism',
      title: t('agents.types.tourism.title'),
      subtitle: t('agents.types.tourism.subtitle'),
      features: t('agents.types.tourism.features', { returnObjects: true }),
      image: agentsSupport
    },
    {
      id: 'health_services',
      title: t('agents.types.health_services.title'),
      subtitle: t('agents.types.health_services.subtitle'),
      features: t('agents.types.health_services.features', { returnObjects: true }),
      image: agentsPlanner
    },
    {
      id: 'custom',
      title: t('agents.types.custom.title'),
      subtitle: t('agents.types.custom.subtitle'),
      features: t('agents.types.custom.features', { returnObjects: true }),
      image: agentsCustom
    }
  ];

  return (
    <RightModalLayout
      title={t('agents.create.title')}
      submitAction={handleSubmit(onCreateAgent)}
      submitLabel={t('agents.create.submit_label')}
      submitDisabled={!nameValue?.trim() || !selectedAgentType || isSubmitting}
    >
      <ModalSection
        title={t('agents.create.section_1_title')}
      >
        <FormProviderMC methods={methods} onSubmit={handleSubmit(onCreateAgent)}>
          <div style={{ marginTop: '8px' }}>
            <InputComponent
              id="name"
              name="name"
              label={t('agents.create.name_label')}
              placeholder={t('agents.create.name_placeholder')}
              useFloatingLabel
            />
          </div>
        </FormProviderMC>
      </ModalSection>

      <ModalSection
        title={t('agents.types.title')}
        description={t('agents.types.description')}
      >
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: '16px',
          marginTop: '8px'
        }}>
          {agentTypes.map((type) => (
            <CardAgent
              key={type.id}
              id={type.id}
              title={type.title}
              subtitle={type.subtitle}
              features={type.features}
              description={type.description}
              image={type.image}
              isSelected={selectedAgentType === type.id}
              onSelect={setSelectedAgentType}
            />
          ))}
        </div>
      </ModalSection>
    </RightModalLayout>
  );
};

export default CreateModal;
